import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, Building2, Heart, GraduationCap, Home, Landmark, Globe, Radio, Plane, Users } from 'lucide-react';
import { motion } from 'framer-motion';

const iconMap = {
  Building2,
  Heart,
  GraduationCap,
  Home,
  Landmark,
  Globe,
  Radio,
  Plane,
  Users,
};

export const SectorsCarousel = ({ sectors, className = '' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'trimSnaps',
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <div className={`relative ${className}`}>
      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-card shadow-lg border border-border hover:bg-primary hover:text-primary-foreground transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={scrollNext}
        className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-card shadow-lg border border-border hover:bg-primary hover:text-primary-foreground transition-colors"
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Carousel */}
      <div className="overflow-hidden px-2" ref={emblaRef}>
        <div className="flex gap-4 md:gap-6">
          {sectors.map((sector, index) => {
            const IconComponent = iconMap[sector.icon] || Building2;
            return (
              <motion.div
                key={sector.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex-shrink-0 w-[280px] md:w-[320px]"
              >
                <div className="group h-full p-6 bg-card rounded-xl border border-border/50 shadow-md hover:shadow-xl hover:border-primary/30 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <IconComponent className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {sector.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {sector.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
